import React, { useState } from 'react';
import { Mail, ArrowLeft, Loader2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import GoogleLogin from '../components/GoogleLogin';
import { LocalLink, useLocalizedNavigate } from '../i18n/useLocalizedNav';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleError, setGoogleError] = useState(false);
  const navigate = useLocalizedNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setGoogleError(false);

    try {
      const res = await api.post('/auth/forgot-password', { email });
      if (res.error) {
        if (res.type === 'GOOGLE_ONLY') {
          setGoogleError(true);
        } else {
          setError(res.error);
        }
      } else {
        navigate('/reset-password', { state: { email } });
      }
    } catch (err) {
      setError(t('auth_err_generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-warm-200 shadow-sm space-y-6">
      <LocalLink to="/login" className="flex items-center text-warm-500 hover:text-warm-800 transition-colors text-sm">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('forgot_back')}
      </LocalLink>

      <div className="text-center space-y-2">
        <h2 className="text-2xl font-heading font-bold text-warm-800">{t('forgot_title')}</h2>
        <p className="text-warm-500 text-sm">{t('forgot_subtitle')}</p>
      </div>

      {googleError ? (
        <div className="space-y-6">
          <div className="bg-brand-accent/10 border border-brand-accent/20 rounded-xl p-4 flex items-start space-x-3">
            <Info className="w-5 h-5 text-brand-accent shrink-0 mt-0.5" />
            <div className="text-sm text-warm-800 leading-relaxed">
              <p className="font-bold mb-1">{t('forgot_google_title')}</p>
              {t('forgot_google_body')}
            </div>
          </div>

          <GoogleLogin />

          <div className="text-center">
            <button
              onClick={() => setGoogleError(false)}
              className="text-xs text-warm-500 hover:text-warm-800 underline uppercase font-bold tracking-widest"
            >
              {t('forgot_try_another')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_email_label')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
              <input 
                type="email" 
                placeholder="name@example.com"
                className="w-full pl-10 pr-4 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div className="bg-brand-danger/10 text-brand-danger p-3 rounded-xl text-xs font-bold border border-brand-danger/20">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all flex items-center justify-center disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('forgot_submit')}
          </button>
        </form>
      )}
    </div>
  );
};

export default ForgotPassword;
